import type { DesignDocument, ResolvedEditAreaDefinition } from '@repo/api-contracts';
import { FACE_KEYS } from '@repo/api-contracts';
import { useEffect, useState } from 'react';
import { collectDocumentAssetIds, emptyDocument } from './document-ops';
import { clearDraftPreview } from './draft-preview-storage';
import { clearDraft, loadDraft } from './draft-storage';
import { useEditorStore } from './editor-store';

export interface InitializeEditorInput {
  readonly productId: string;
  /** 최신 공개 편집영역. 새 버전으로 바뀌어도 초기화하지 않는다 — 문서는 mm 라 그대로 열린다(정책 §3-1). */
  readonly definition: ResolvedEditAreaDefinition | null;
  /** 상품 색상(로드 전 undefined). */
  readonly colorOptions:
    | readonly { readonly id: string; readonly isAvailable: boolean }[]
    | undefined;
  readonly savedDesign: (DesignDocument & { readonly id: string }) | undefined;
  readonly designId: string | null;
  /**
   * 상세에서 고른 색상(`?color=`). 새 문서를 열 때만, 그 색상이 수급 가능하고 편집영역에 면 사진이 있을 때만 처음 색상으로 쓴다 —
   * 아니면 기본 색상 규칙으로 돌아간다. 저장 디자인·임시 보관본을 열 때는 무시한다(그 문서의 색상 그대로).
   */
  readonly preferredColorId?: string | null;
  readonly isAuthenticated: boolean;
}

/**
 * 에디터 스토어 초기화(U2/U3). 이 마운트에서 상품별로 한 번만 한다 — 저장 뒤 `?designId=` 로 바뀌거나 편집영역을 다시 받아도
 * history·선택·면을 지우지 않는다. 다시 초기화하는 경우는 스토어와 다른 id 의 저장 디자인이 로드됐을 때뿐이다.
 * 로그인 상태로 `?designId=` 를 열면 저장 디자인이 올 때까지 초기화하지 않는다(조회 실패 포함 — 안내는 페이지가 한다).
 * 한 번 초기화되면 initialized 는 디자인 조회 대기로 다시 false 가 되지 않는다.
 * restoredDraft 는 localStorage 보관본(draft-storage)으로 열었고 아직 저장하지 않았을 때 참이다.
 * 회원(로그인 상태)은 이미지가 든 보관본을 복원하지 않고 지운다(G13) — 그 이미지는 비회원 키 소유라 회원 API 로 읽거나 저장할 수 없다.
 */
export const useInitializeEditor = ({
  productId,
  definition,
  colorOptions,
  savedDesign,
  designId,
  preferredColorId = null,
  isAuthenticated,
}: InitializeEditorInput): { initialized: boolean; restoredDraft: boolean } => {
  const storeDesignId = useEditorStore((state) => state.designId);
  const initialize = useEditorStore((state) => state.initialize);
  const [initializedFor, setInitializedFor] = useState<string | null>(null);
  const [draftRestoredFor, setDraftRestoredFor] = useState<string | null>(null);
  const initialized = initializedFor === productId;
  // 복원한 보관본은 저장해 designId 가 생기기 전까지만 '불러왔음' 으로 본다(§6-5 안내).
  const restoredDraft = draftRestoredFor === productId && storeDesignId === null;

  useEffect(() => {
    if (definition === null || colorOptions === undefined) return;
    const loaded = savedDesign !== undefined && savedDesign.id === designId ? savedDesign : null;
    if (initialized) {
      // 저장 직후(markSaved 가 이미 id 를 적음)엔 같은 id 라 건너뛴다.
      if (loaded === null || loaded.id === storeDesignId) return;
    } else if (designId !== null && isAuthenticated && loaded === null) {
      // 저장 디자인을 여는 중이거나 조회에 실패했다 — 빈 문서로 열면 저장이 새 디자인을 만들므로 기다린다(실패 안내는 페이지 몫).
      return;
    }
    const faceKey =
      FACE_KEYS.find((key) => (definition.faces[key]?.placements.length ?? 0) > 0) ?? 'front';
    const componentId = definition.faces[faceKey]?.placements[0]?.componentId ?? '';
    // 수급 가능하고 편집영역에 면 사진이 있는 첫 색상 — 사진 없는 색상으로 열면 미리보기를 만들 수 없다.
    const hasPhotos = (colorOptionId: string): boolean =>
      definition.colors.some(
        (entry) =>
          entry.colorOptionId === colorOptionId &&
          Object.values(entry.faces).some((face) => face !== undefined),
      );
    const isUsable = (option: { readonly id: string; readonly isAvailable: boolean }): boolean =>
      option.isAvailable && hasPhotos(option.id);
    // 상세에서 고른 색상이 같은 조건을 만족하면 그 색상, 아니면 기본 규칙 — 빈 문서에만 쓰인다(보관본·저장본은 제 색상).
    const preferred = colorOptions.find(
      (option) => option.id === preferredColorId && isUsable(option),
    );
    const defaultColor =
      preferred?.id ?? colorOptions.find(isUsable)?.id ?? definition.colors[0]?.colorOptionId ?? '';
    const stored = designId === null ? loadDraft(productId) : null;
    const discardDraft =
      stored !== null && isAuthenticated && collectDocumentAssetIds(stored).length > 0;
    if (discardDraft) {
      clearDraft(productId);
      clearDraftPreview(productId);
    }
    const draft = discardDraft ? null : stored;
    const document: DesignDocument =
      loaded !== null
        ? {
            productId: loaded.productId,
            colorOptionId: loaded.colorOptionId,
            components: loaded.components,
          }
        : (draft ?? emptyDocument(productId, defaultColor));
    initialize({ document, designId: loaded?.id ?? null, faceKey, componentId });
    setInitializedFor(productId);
    setDraftRestoredFor(loaded === null && draft !== null ? productId : null);
  }, [
    productId,
    definition,
    colorOptions,
    savedDesign,
    designId,
    preferredColorId,
    isAuthenticated,
    initialized,
    storeDesignId,
    initialize,
  ]);

  return { initialized, restoredDraft };
};
